// src/pages/public/Applications.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
  getApplications, createApplication, updateApplication,
  updateStatut, toggleApplication, deleteApplication,
  uploadLogo
} from '../../services/applicationService';
import {
  FiZap, FiSearch, FiArrowRight, FiCheckCircle, FiStar, FiUsers,
  FiPackage, FiMap, FiLink2, FiBook, FiDollarSign, FiShield, FiAward,
  FiExternalLink, FiInfo, FiX, FiGrid, FiList, FiCpu, FiDatabase,
  FiBarChart2, FiSend, FiPlus, FiEdit2, FiTrash2, FiEyeOff, FiEye,
  FiSave, FiAlertTriangle, FiRefreshCw, FiUpload, FiImage
} from 'react-icons/fi';

const ICON_MAP = {
  FiShield, FiBarChart2, FiBook, FiDollarSign, FiMap, FiLink2,
  FiPackage, FiZap, FiCpu, FiDatabase, FiStar, FiUsers, FiAward,
  FiSearch, FiGrid, FiList, FiSend, FiExternalLink,
};
const ICONS_DISPONIBLES = Object.keys(ICON_MAP);
const getIcon = (nom) => ICON_MAP[nom] || FiZap;

const CATEGORIES = ['Toutes', 'Évaluation', 'Suivi', 'Formation', 'Finance', 'Cartographie', 'Mise en relation'];
const STATUTS = ['dispo', 'beta', 'dev', 'archive'];
const STATUT_CONFIG = {
  dispo:   { label: 'Disponible',        color: 'bg-emerald-500' },
  beta:    { label: 'Bêta',             color: 'bg-amber-400' },
  dev:     { label: 'En développement', color: 'bg-slate-400' },
  archive: { label: 'Archivé',          color: 'bg-red-400' },
};
const COULEURS_PRESET = [
  { key: 'blue',   bg: 'from-blue-600 to-blue-800',      badge: 'bg-blue-100 text-blue-700',       accent: 'text-blue-600',    border: 'border-blue-200' },
  { key: 'green',  bg: 'from-emerald-600 to-green-800',  badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600', border: 'border-emerald-200' },
  { key: 'purple', bg: 'from-purple-600 to-purple-800',  badge: 'bg-purple-100 text-purple-700',   accent: 'text-purple-600',  border: 'border-purple-200' },
  { key: 'amber',  bg: 'from-amber-500 to-orange-700',   badge: 'bg-amber-100 text-amber-700',     accent: 'text-amber-600',   border: 'border-amber-200' },
  { key: 'teal',   bg: 'from-teal-600 to-teal-800',      badge: 'bg-teal-100 text-teal-700',       accent: 'text-teal-600',    border: 'border-teal-200' },
  { key: 'indigo', bg: 'from-indigo-600 to-indigo-800',  badge: 'bg-indigo-100 text-indigo-700',   accent: 'text-indigo-600',  border: 'border-indigo-200' },
  { key: 'rose',   bg: 'from-rose-600 to-rose-800',      badge: 'bg-rose-100 text-rose-700',       accent: 'text-rose-600',    border: 'border-rose-200' },
  { key: 'slate',  bg: 'from-slate-600 to-slate-800',    badge: 'bg-slate-100 text-slate-700',     accent: 'text-slate-600',   border: 'border-slate-200' },
];
const getPreset = (key) => COULEURS_PRESET.find(c => c.key === key) || COULEURS_PRESET[0];

const FORM_EMPTY = {
  nom: '', sous_titre: '', description: '', fonctionnalites: '',
  icone_nom: 'FiZap', categorie: 'Évaluation', couleur: 'blue',
  statut: 'dev', public_cible: '', prix_depart: '', offre_gratuite: '',
  demo_dispo: false, demo_url: '', ordre: 0, actif: true,
  logo_url: ''
};

const INPUT = "w-full text-sm p-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none bg-white";
const LABEL = "block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5";

const Toggle = ({ val, onToggle }) => (
  <button type="button" onClick={onToggle}
    className={`w-10 h-6 rounded-full transition-colors ${val ? 'bg-emerald-500' : 'bg-gray-300'} relative flex-shrink-0`}>
    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${val ? 'left-4' : 'left-0.5'}`} />
  </button>
);

// ===== StatutBadge =====
const StatutBadge = ({ statut }) => {
  const c = STATUT_CONFIG[statut] || STATUT_CONFIG.dev;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${c.color} text-white`}>
      {statut === 'dispo' && <FiCheckCircle className="w-3 h-3" />}
      {statut === 'beta'  && <FiZap className="w-3 h-3" />}
      {statut === 'archive' && <FiEyeOff className="w-3 h-3" />}
      {c.label}
    </span>
  );
};

// ===== Alerte flash =====
const Alert = ({ msg, type, onClose }) => !msg ? null : (
  <motion.div initial={{ opacity:0, x:50 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:50 }}
    className={`fixed top-5 right-5 z-[60] max-w-sm flex items-start gap-3 p-4 rounded-xl shadow-xl border backdrop-blur-sm ${
      type==='error' ? 'bg-red-50/95 text-red-800 border-red-200' : 'bg-emerald-50/95 text-emerald-800 border-emerald-200'
    }`}>
    {type==='error' ? <FiAlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0"/> : <FiCheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0"/>}
    <p className="text-sm font-medium flex-1">{msg}</p>
    <button onClick={onClose}><FiX className="w-4 h-4 opacity-50 hover:opacity-100"/></button>
  </motion.div>
);

// ===== Modal détail =====
const DetailModal = ({ app, isAdmin, onClose, onEdit }) => {
  if (!app) return null;
  const Icon = getIcon(app.icone_nom);
  const p = getPreset(app.couleur);
  const hasLogo = app.logo_url && app.logo_url.length > 0;
  
  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{scale:0.95,opacity:0,y:20}} animate={{scale:1,opacity:1,y:0}} exit={{scale:0.95,opacity:0}}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e=>e.stopPropagation()}>
        <div className={`bg-gradient-to-r ${p.bg} p-6 rounded-t-2xl relative overflow-hidden`}>
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:12px_12px]"/>
          <div className="relative z-10 flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl ${hasLogo ? 'bg-white p-2' : 'bg-white/20'} flex items-center justify-center border border-white/30`}>
                {hasLogo ? (
                  <img src={app.logo_url} alt={app.nom} className="w-full h-full object-contain rounded-xl" />
                ) : (
                  <Icon className="w-8 h-8 text-white"/>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-black text-white">{app.nom}</h2>
                <p className="text-white/80 text-sm">{app.sous_titre}</p>
              </div>
            </div>
            <div className="flex gap-1.5">
              {isAdmin && (
                <button onClick={()=>{onClose();onEdit(app);}} className="text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-xl" title="Modifier">
                  <FiEdit2 className="w-4 h-4"/>
                </button>
              )}
              <button onClick={onClose} className="text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-xl">
                <FiX className="w-5 h-5"/>
              </button>
            </div>
          </div>
          <div className="relative z-10 mt-4 flex gap-2">
            <StatutBadge statut={app.statut}/>
            <span className="text-xs px-2 py-0.5 rounded-full bg-white/20 text-white font-medium">{app.categorie}</span>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-600 leading-relaxed">{app.description}</p>
          {app.fonctionnalites?.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Fonctionnalités</h3>
              <ul className="space-y-2">
                {app.fonctionnalites.map((f,i)=>(
                  <li key={i} className={`flex items-start gap-2.5 text-sm text-gray-700`}>
                    <FiCheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.accent}`}/>{f}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-3.5 rounded-xl border ${p.border} bg-gray-50/50`}>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Public cible</p>
              <p className="text-xs font-semibold text-gray-700">{app.public_cible||'—'}</p>
            </div>
            <div className={`p-3.5 rounded-xl border ${p.border} bg-gray-50/50`}>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Tarif de départ</p>
              <p className={`text-xs font-bold ${p.accent}`}>{app.prix_depart||'—'}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{app.offre_gratuite}</p>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            {app.demo_dispo ? (
              <a href={app.demo_url||'#'} target="_blank" rel="noopener noreferrer"
                className={`flex-1 bg-gradient-to-r ${p.bg} text-white text-sm font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 shadow-lg`}>
                <FiExternalLink className="w-4 h-4"/> Accéder à l'application
              </a>
            ) : (
              <button className="flex-1 bg-gray-100 text-gray-400 text-sm font-bold py-3 rounded-xl flex items-center justify-center gap-2 cursor-not-allowed">
                <FiCpu className="w-4 h-4"/> En cours de développement
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ===== Modal formulaire admin avec upload Cloudinary =====
const FormModal = ({ app, onClose, onSaved, setAlert }) => {
  const isEdit = !!app?.id;
  const [form, setForm] = useState(() => {
    if (!app) return { ...FORM_EMPTY };
    return { ...FORM_EMPTY, ...app,
      fonctionnalites: Array.isArray(app.fonctionnalites) ? app.fonctionnalites.join('\n') : (app.fonctionnalites||''),
      logo_url: app.logo_url || ''
    };
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(form.logo_url || '');
  const fileInputRef = useRef(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const p = getPreset(form.couleur);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      setAlert('Format non supporté. Utilisez JPEG, PNG, WEBP ou SVG.', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setAlert('L\'image ne doit pas dépasser 2MB.', 'error');
      return;
    }

    setUploading(true);
    try {
      const imageUrl = await uploadLogo(file);
      setForm(prev => ({ ...prev, logo_url: imageUrl }));
      setPreview(imageUrl);
      setAlert('Logo téléchargé avec succès ✓', 'success');
    } catch (error) {
      console.error('Erreur upload:', error);
      setAlert('Erreur lors du téléchargement du logo.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setForm(prev => ({ ...prev, logo_url: '' }));
    setPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()||!form.description.trim()||!form.categorie) {
      setAlert('Nom, description et catégorie sont requis.', 'error'); return;
    }
    setLoading(true);
    try {
      const payload = { ...form,
        fonctionnalites: form.fonctionnalites.split('\n').map(f=>f.trim()).filter(Boolean),
        demo_dispo: !!form.demo_dispo, ordre: Number(form.ordre)||0, actif: !!form.actif,
        logo_url: form.logo_url || null
      };
      const saved = isEdit ? await updateApplication(app.id, payload) : await createApplication(payload);
      onSaved(saved, isEdit);
      onClose();
      setAlert(isEdit ? 'Application mise à jour ✓' : 'Application créée ✓', 'success');
    } catch(err) {
      setAlert(err.response?.data?.error||'Erreur lors de la sauvegarde.', 'error');
    } finally { setLoading(false); }
  };

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{scale:0.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.95,opacity:0}}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e=>e.stopPropagation()}>
        <div className={`bg-gradient-to-r ${p.bg} p-5 rounded-t-2xl flex items-center justify-between`}>
          <div>
            <h3 className="text-white font-black text-lg">{isEdit?`Modifier — ${app.nom}`:'Nouvelle application'}</h3>
            <p className="text-white/70 text-xs">Espace admin AIFASA 17</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-xl"><FiX className="w-5 h-5"/></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Logo upload */}
          <div className="bg-gray-50 rounded-xl p-4 border-2 border-dashed border-gray-200">
            <label className={`${LABEL} flex items-center gap-2`}>
              <FiImage className="w-4 h-4"/> Logo de l'application
            </label>
            
            {preview ? (
              <div className="relative inline-block">
                <img 
                  src={preview} 
                  alt="Logo" 
                  className="w-20 h-20 object-contain rounded-xl border border-gray-200 bg-white p-2"
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-lg"
                >
                  <FiX className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-gray-200 rounded-xl flex items-center justify-center text-gray-400">
                  <FiImage className="w-8 h-8" />
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full"/>
                    Upload...
                  </>
                ) : (
                  <>
                    <FiUpload className="w-4 h-4"/>
                    Choisir un fichier
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                onChange={handleFileChange}
                className="hidden"
              />
              <span className="text-[10px] text-gray-400">PNG, JPEG, WEBP ou SVG • Max 2MB</span>
            </div>
          </div>

          {/* Identité */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={LABEL}>Nom *</label>
              <input value={form.nom} onChange={e=>set('nom',e.target.value)} placeholder="Ex : SOIL-AIFASA" className={INPUT} required/>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Sous-titre</label>
              <input value={form.sous_titre} onChange={e=>set('sous_titre',e.target.value)} placeholder="Ex : Analyse de qualité des sols" className={INPUT}/>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Description * (2–4 phrases)</label>
              <textarea rows={3} value={form.description} onChange={e=>set('description',e.target.value)} className={INPUT+" resize-none"} required/>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>Fonctionnalités (une par ligne)</label>
              <textarea rows={5} value={form.fonctionnalites} onChange={e=>set('fonctionnalites',e.target.value)}
                placeholder={"Analyse pH en temps réel\nCarte de fertilité\nExport rapport PDF"} className={INPUT+" resize-none font-mono text-xs"}/>
            </div>
          </div>

          {/* Catégorie / statut / icône */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={LABEL}>Catégorie *</label>
              <select value={form.categorie} onChange={e=>set('categorie',e.target.value)} className={INPUT}>
                {CATEGORIES.filter(c=>c!=='Toutes').map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Statut</label>
              <select value={form.statut} onChange={e=>set('statut',e.target.value)} className={INPUT}>
                {STATUTS.map(s=><option key={s} value={s}>{STATUT_CONFIG[s].label}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Icône (fallback)</label>
              <select value={form.icone_nom} onChange={e=>set('icone_nom',e.target.value)} className={INPUT}>
                {ICONS_DISPONIBLES.map(ic=><option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
          </div>

          {/* Couleur */}
          <div>
            <label className={LABEL}>Couleur</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {COULEURS_PRESET.map(c=>(
                <button key={c.key} type="button" onClick={()=>set('couleur',c.key)}
                  className={`w-8 h-8 rounded-full bg-gradient-to-br ${c.bg} transition-all ${form.couleur===c.key?'ring-2 ring-offset-2 ring-gray-400 scale-110':'opacity-60 hover:opacity-100'}`} title={c.key}/>
              ))}
            </div>
          </div>

          {/* Public / Prix */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={LABEL}>Public cible</label>
              <input value={form.public_cible} onChange={e=>set('public_cible',e.target.value)} placeholder="Ex : PME agricoles, planteurs" className={INPUT}/>
            </div>
            <div>
              <label className={LABEL}>Tarif de départ</label>
              <input value={form.prix_depart} onChange={e=>set('prix_depart',e.target.value)} placeholder="Ex : 25 000 FCFA/mois" className={INPUT}/>
            </div>
            <div>
              <label className={LABEL}>Offre gratuite</label>
              <input value={form.offre_gratuite} onChange={e=>set('offre_gratuite',e.target.value)} placeholder="Ex : 2 cours inclus" className={INPUT}/>
            </div>
          </div>

          {/* Démo */}
          <div className="grid grid-cols-3 gap-4 items-end">
            <div className="flex items-center gap-3">
              <span className={LABEL.replace('mb-1.5','')}>Démo disponible</span>
              <Toggle val={form.demo_dispo} onToggle={()=>set('demo_dispo',!form.demo_dispo)}/>
            </div>
            <div className="col-span-2">
              <label className={LABEL}>URL de la démo</label>
              <input value={form.demo_url} onChange={e=>set('demo_url',e.target.value)} placeholder="https://..." type="url" className={INPUT} disabled={!form.demo_dispo}/>
            </div>
          </div>

          {/* Ordre / Actif */}
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className={LABEL}>Ordre d'affichage</label>
              <input type="number" min="0" value={form.ordre} onChange={e=>set('ordre',e.target.value)} className={INPUT}/>
            </div>
            <div className="flex items-center gap-3">
              <span className={LABEL.replace('mb-1.5','')}>Visible (actif)</span>
              <Toggle val={form.actif} onToggle={()=>set('actif',!form.actif)}/>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-3 border-t border-gray-100">
            <button type="submit" disabled={loading}
              className={`flex-1 bg-gradient-to-r ${p.bg} text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 shadow-lg disabled:opacity-50`}>
              <FiSave className="w-4 h-4"/>
              {loading?'Enregistrement...':(isEdit?'Mettre à jour':'Créer l\'application')}
            </button>
            <button type="button" onClick={onClose} className="px-5 py-3 border border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50">Annuler</button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// ===== Carte =====
const AppCard = ({ app, vue, isAdmin, onClick, onEdit, onToggle, onStatut, onDelete }) => {
  const Icon = getIcon(app.icone_nom);
  const p = getPreset(app.couleur);
  const isGrid = vue === 'grid';
  const hasLogo = app.logo_url && app.logo_url.length > 0;

  if (!isGrid) return (
    <motion.div layout initial={{opacity:0,x:-20}} animate={{opacity:1,x:0}}
      className={`bg-white rounded-xl border ${p.border} shadow-sm hover:shadow-md transition-all flex items-center gap-5 p-4 ${!app.actif?'opacity-50':''}`}>
      <div className={`w-12 h-12 rounded-xl ${hasLogo ? 'bg-white p-1 border border-gray-200' : `bg-gradient-to-br ${p.bg}`} flex items-center justify-center flex-shrink-0 shadow-md cursor-pointer hover:scale-105 transition-transform`} onClick={onClick}>
        {hasLogo ? (
          <img src={app.logo_url} alt={app.nom} className="w-full h-full object-contain rounded-lg" />
        ) : (
          <Icon className="w-6 h-6 text-white"/>
        )}
      </div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-black text-gray-900 text-sm">{app.nom}</span>
          <StatutBadge statut={app.statut}/>
          {!app.actif && <span className="text-[10px] text-red-500 font-bold">INACTIF</span>}
        </div>
        <p className="text-xs text-gray-500 truncate">{app.description?.substring(0,90)}...</p>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className={`text-xs font-bold ${p.accent}`}>{app.prix_depart}</span>
        <span className="text-[10px] text-gray-400">{app.offre_gratuite}</span>
      </div>
      {isAdmin && (
        <div className="flex gap-1 flex-shrink-0" onClick={e=>e.stopPropagation()}>
          <button onClick={()=>onEdit(app)} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Modifier"><FiEdit2 className="w-3.5 h-3.5"/></button>
          <button onClick={()=>onToggle(app)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg" title={app.actif?'Désactiver':'Activer'}>
            {app.actif?<FiEyeOff className="w-3.5 h-3.5"/>:<FiEye className="w-3.5 h-3.5"/>}
          </button>
        </div>
      )}
      <FiArrowRight className="w-4 h-4 text-gray-300 cursor-pointer" onClick={onClick}/>
    </motion.div>
  );

  return (
    <motion.div layout initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
      className={`bg-white rounded-2xl border ${p.border} shadow-sm hover:shadow-xl transition-all flex flex-col overflow-hidden group ${!app.actif?'opacity-60':''}`}>
      <div className={`relative bg-gradient-to-br ${p.bg} p-6 overflow-hidden cursor-pointer`} onClick={onClick}>
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:12px_12px]"/>
        <div className="relative z-10 flex items-start justify-between mb-4">
          <div className={`w-16 h-16 ${hasLogo ? 'bg-white p-2 rounded-2xl' : 'bg-white/20 rounded-2xl'} flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform`}>
            {hasLogo ? (
              <img src={app.logo_url} alt={app.nom} className="w-full h-full object-contain rounded-xl" />
            ) : (
              <Icon className="w-8 h-8 text-white"/>
            )}
          </div>
          <div className="flex items-center gap-1">
            <StatutBadge statut={app.statut}/>
            {!app.actif && <span className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded-full font-bold">INACTIF</span>}
          </div>
        </div>
        <div className="relative z-10">
          <h3 className="font-black text-white text-lg">{app.nom}</h3>
          <p className="text-white/80 text-xs">{app.sous_titre}</p>
        </div>
        {isAdmin && (
          <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20" onClick={e=>e.stopPropagation()}>
            <button onClick={()=>onEdit(app)} className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-lg backdrop-blur-sm" title="Modifier"><FiEdit2 className="w-3.5 h-3.5"/></button>
            <button onClick={()=>onToggle(app)} className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-lg backdrop-blur-sm" title={app.actif?'Désactiver':'Activer'}>
              {app.actif?<FiEyeOff className="w-3.5 h-3.5"/>:<FiEye className="w-3.5 h-3.5"/>}
            </button>
            <button onClick={()=>{if(window.confirm(`Supprimer "${app.nom}" ?`))onDelete(app);}} className="p-1.5 bg-red-500/70 hover:bg-red-600 text-white rounded-lg backdrop-blur-sm" title="Supprimer"><FiTrash2 className="w-3.5 h-3.5"/></button>
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${p.accent}`}>{app.categorie}</span>
          {isAdmin && (
            <select value={app.statut} onChange={e=>onStatut(app,e.target.value)} onClick={e=>e.stopPropagation()}
              className="text-[10px] border border-gray-200 rounded-lg px-1.5 py-0.5 text-gray-600 focus:outline-none cursor-pointer">
              {STATUTS.map(s=><option key={s} value={s}>{STATUT_CONFIG[s].label}</option>)}
            </select>
          )}
        </div>
        <p className="text-xs text-gray-500 leading-relaxed mb-4 flex-1 line-clamp-3 cursor-pointer" onClick={onClick}>{app.description}</p>
        {app.fonctionnalites?.length>0 && (
          <ul className="space-y-1.5 mb-4">
            {app.fonctionnalites.slice(0,3).map((f,i)=>(
              <li key={i} className="flex items-center gap-2 text-[11px] text-gray-600">
                <FiCheckCircle className={`w-3 h-3 flex-shrink-0 ${p.accent}`}/>{f}
              </li>
            ))}
            {app.fonctionnalites.length>3 && <li className="text-[11px] text-gray-400 pl-5">+{app.fonctionnalites.length-3} autres...</li>}
          </ul>
        )}
        <div className={`p-3 rounded-xl border ${p.border} bg-gray-50/50 mb-4 flex items-center justify-between`}>
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">À partir de</p>
            <p className={`text-sm font-black ${p.accent}`}>{app.prix_depart||'—'}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-400">Offre gratuite</p>
            <p className="text-[11px] text-gray-600 font-semibold">{app.offre_gratuite||'—'}</p>
          </div>
        </div>
        <button onClick={onClick} className={`w-full bg-gradient-to-r ${p.bg} text-white text-xs font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 shadow-md`}>
          {app.demo_dispo?<><FiExternalLink className="w-3.5 h-3.5"/>Voir l'application</>:<><FiCpu className="w-3.5 h-3.5"/>En développement</>}
        </button>
      </div>
    </motion.div>
  );
};

// ===== PAGE PRINCIPALE =====
const Applications = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [categorie, setCategorie] = useState('Toutes');
  const [vue, setVue] = useState('grid');
  const [appDetail, setAppDetail] = useState(null);
  const [appEdit, setAppEdit] = useState(null);
  const [showProposer, setShowProposer] = useState(false);
  const [alert, setAlert] = useState({ message: '', type: '' });

  const triggerAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert({ message: '', type: '' }), 4000);
  };

  const loadApps = useCallback(async () => {
    setLoading(true);
    try { 
      const data = await getApplications({ all: isAdmin });
      setApps(data);
    } catch (err) {
      console.error('Erreur chargement:', err);
      triggerAlert('Impossible de charger les applications.', 'error');
    } finally { 
      setLoading(false); 
    }
  }, [isAdmin]);

  useEffect(() => { loadApps(); }, [loadApps]);

  const filtered = apps.filter(app => {
    if (!isAdmin && !app.actif) return false;
    const matchCat = categorie === 'Toutes' || app.categorie === categorie;
    const matchSearch = !recherche ||
      app.nom?.toLowerCase().includes(recherche.toLowerCase()) ||
      app.description?.toLowerCase().includes(recherche.toLowerCase()) ||
      app.categorie?.toLowerCase().includes(recherche.toLowerCase());
    return matchCat && matchSearch;
  });

  const stats = {
    total: apps.filter(a=>isAdmin||a.actif).length,
    dispo: apps.filter(a=>a.statut==='dispo'&&(isAdmin||a.actif)).length,
    beta:  apps.filter(a=>a.statut==='beta' &&(isAdmin||a.actif)).length,
    dev:   apps.filter(a=>a.statut==='dev'  &&(isAdmin||a.actif)).length,
  };

  const handleSaved = (saved, isEdit) =>
    setApps(prev => isEdit ? prev.map(a=>a.id===saved.id?saved:a) : [saved,...prev]);

  const handleToggle = async (app) => {
    try {
      const u = await toggleApplication(app.id);
      setApps(prev=>prev.map(a=>a.id===u.id?u:a));
      triggerAlert(`${u.nom} — ${u.actif?'activée':'désactivée'}`);
    } catch { triggerAlert('Erreur toggle.','error'); }
  };
  
  const handleStatut = async (app, statut) => {
    try {
      const u = await updateStatut(app.id, statut);
      setApps(prev=>prev.map(a=>a.id===u.id?u:a));
      triggerAlert(`Statut → ${STATUT_CONFIG[statut].label}`);
    } catch { triggerAlert('Erreur statut.','error'); }
  };
  
  const handleDelete = async (app) => {
    try {
      await deleteApplication(app.id);
      setApps(prev=>prev.filter(a=>a.id!==app.id));
      triggerAlert(`${app.nom} supprimée.`);
    } catch { triggerAlert('Erreur suppression.','error'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50/20 overflow-x-hidden">
      <AnimatePresence>
        {alert.message && <Alert msg={alert.message} type={alert.type} onClose={()=>setAlert({message:'',type:''})}/>}
      </AnimatePresence>

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-green-950 via-green-900 to-emerald-950 text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-[-40%] left-[-10%] w-[500px] h-[500px] bg-emerald-400/30 rounded-full blur-3xl animate-pulse"/>
          <div className="absolute bottom-[-20%] right-[-5%] w-[400px] h-[400px] bg-teal-300/20 rounded-full blur-3xl animate-pulse delay-700"/>
        </div>
        <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"/>
        <div className="container mx-auto max-w-6xl relative z-10 text-center">
          <motion.div initial={{opacity:0,y:-24}} animate={{opacity:1,y:0}} transition={{duration:0.6}}>
            <div className="inline-flex items-center gap-2 bg-emerald-400/20 border border-emerald-400/30 text-emerald-300 text-xs font-bold px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
              <FiCpu className="w-3.5 h-3.5"/> Hub Numérique AIFASA 17
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">Outils & <span className="text-emerald-300">Applications</span></h1>
            <div className="h-1 w-24 bg-gradient-to-r from-emerald-400 to-teal-300 mx-auto my-4 rounded-full"/>
            <p className="text-lg md:text-xl text-green-100 max-w-2xl mx-auto font-light leading-relaxed mb-8">Des outils numériques conçus par et pour les ingénieurs agronomes et forestiers.</p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
              {[
                {label:'Applications',value:stats.total,icon:FiGrid},
                {label:'Disponibles',value:stats.dispo,icon:FiCheckCircle},
                {label:'En bêta',value:stats.beta,icon:FiZap},
                {label:'En cours',value:stats.dev,icon:FiCpu}
              ].map((s,i)=>(
                <motion.div key={i} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:i*0.1}}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2.5 rounded-xl">
                  <s.icon className="w-4 h-4 text-emerald-300"/>
                  <span className="text-2xl font-black text-white">{s.value}</span>
                  <span className="text-xs text-green-200 font-medium">{s.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FILTRES */}
      <section className="container mx-auto max-w-6xl px-4 py-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-64">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-400 w-4 h-4"/>
            <input type="text" placeholder="Rechercher..." value={recherche} onChange={e=>setRecherche(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"/>
          </div>
          <div className="flex flex-wrap gap-2 flex-1 justify-center">
            {CATEGORIES.map(cat=>(
              <button key={cat} onClick={()=>setCategorie(cat)}
                className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-all ${categorie===cat?'bg-emerald-700 text-white shadow-md':'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'}`}>
                {cat}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              <button onClick={()=>setVue('grid')} className={`p-2 rounded-md transition-all ${vue==='grid'?'bg-white shadow-sm text-emerald-700':'text-gray-400'}`}><FiGrid className="w-4 h-4"/></button>
              <button onClick={()=>setVue('list')} className={`p-2 rounded-md transition-all ${vue==='list'?'bg-white shadow-sm text-emerald-700':'text-gray-400'}`}><FiList className="w-4 h-4"/></button>
            </div>
            {isAdmin && <>
              <button onClick={loadApps} className="p-2 bg-gray-100 rounded-lg text-gray-500 hover:bg-gray-200" title="Rafraîchir"><FiRefreshCw className="w-4 h-4"/></button>
              <button onClick={()=>setAppEdit({})} className="flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-md">
                <FiPlus className="w-4 h-4"/> Nouvelle App
              </button>
            </>}
          </div>
        </div>
      </section>

      {/* GRILLE */}
      <section className="container mx-auto max-w-6xl px-4 pb-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent mx-auto mb-4"/>
            <p className="text-gray-400 text-sm">Chargement des applications...</p>
          </div>
        ) : filtered.length===0 ? (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="bg-white rounded-2xl p-16 text-center shadow-sm border border-gray-100">
            <FiDatabase className="w-14 h-14 text-gray-200 mx-auto mb-4"/>
            <p className="text-gray-500 font-semibold">Aucune application ne correspond.</p>
            {isAdmin && <button onClick={()=>setAppEdit({})} className="mt-4 bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-emerald-800"><FiPlus className="inline w-4 h-4 mr-1.5"/>Ajouter la première</button>}
          </motion.div>
        ) : (
          <motion.div layout className={vue==='grid'?'grid sm:grid-cols-2 lg:grid-cols-3 gap-6':'flex flex-col gap-3'}>
            <AnimatePresence>
              {filtered.map(app=>(
                <AppCard key={app.id} app={app} vue={vue} isAdmin={isAdmin}
                  onClick={()=>setAppDetail(app)} onEdit={a=>setAppEdit(a)}
                  onToggle={handleToggle} onStatut={handleStatut} onDelete={handleDelete}/>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </section>

      {/* ACCÈS */}
      <section className="bg-gradient-to-br from-emerald-950 to-green-950 text-white py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black mb-2">Accès & Abonnements</h2>
            <p className="text-green-200 text-sm max-w-xl mx-auto">Chaque outil est accessible selon votre profil.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              {label:'Visiteur',desc:'Démonstrations et aperçus libres',prix:'Gratuit',icon:FiUsers,color:'bg-white/10 border-white/20'},
              {label:'Membre actif',desc:'Outils de base inclus dans la cotisation',prix:'Cotisation annuelle',icon:FiAward,color:'bg-emerald-700/40 border-emerald-500/30'},
              {label:'Membre premium',desc:'Toutes applications, toutes fonctionnalités',prix:'Cotisation + abonnement',icon:FiStar,color:'bg-amber-600/30 border-amber-400/30'},
              {label:'Institutionnel',desc:'Licences multi-utilisateurs, données brutes, API',prix:'Convention annuelle',icon:FiShield,color:'bg-purple-700/30 border-purple-400/30'},
            ].map((tier,i)=>(
              <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} transition={{delay:i*0.1}} className={`${tier.color} border rounded-2xl p-5 backdrop-blur-sm`}>
                <tier.icon className="w-7 h-7 text-emerald-300 mb-3"/>
                <h3 className="font-bold text-white text-base mb-1">{tier.label}</h3>
                <p className="text-green-200 text-xs leading-relaxed mb-3">{tier.desc}</p>
                <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">{tier.prix}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA PROPOSER */}
      <section className="container mx-auto max-w-6xl px-4 py-16">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2"><FiZap className="w-5 h-5 text-amber-500"/><span className="text-xs font-bold text-amber-600 uppercase tracking-wider">Vous avez développé un outil ?</span></div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">Proposez votre application</h3>
            <p className="text-gray-500 text-sm max-w-md">Tout membre peut soumettre un outil. Après validation, votre application reçoit le label AIFASA-APP et est référencée avec un partage de revenus.</p>
          </div>
          <button onClick={()=>setShowProposer(true)} className="flex-shrink-0 bg-gradient-to-r from-emerald-700 to-green-700 hover:opacity-90 text-white font-bold px-8 py-4 rounded-xl flex items-center gap-2 shadow-lg">
            <FiSend className="w-5 h-5"/> Soumettre une application
          </button>
        </motion.div>
      </section>

      {/* MODALES */}
      <AnimatePresence>
        {appDetail && <DetailModal app={appDetail} isAdmin={isAdmin} onClose={()=>setAppDetail(null)} onEdit={a=>{setAppDetail(null);setAppEdit(a);}}/>}
      </AnimatePresence>
      <AnimatePresence>
        {appEdit!==null && <FormModal app={Object.keys(appEdit).length>0?appEdit:null} onClose={()=>setAppEdit(null)} onSaved={handleSaved} setAlert={triggerAlert}/>}
      </AnimatePresence>
      <AnimatePresence>
        {showProposer && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={()=>setShowProposer(false)}>
            <motion.div initial={{scale:0.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.95,opacity:0}}
              className="bg-white rounded-2xl max-w-lg w-full shadow-2xl" onClick={e=>e.stopPropagation()}>
              <div className="bg-gradient-to-r from-emerald-700 to-green-700 p-6 rounded-t-2xl flex items-center justify-between">
                <div><h3 className="text-white font-black text-xl">Proposer une application</h3><p className="text-emerald-200 text-sm">Soumission au Bureau Exécutif AIFASA 17</p></div>
                <button onClick={()=>setShowProposer(false)} className="text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-xl"><FiX className="w-5 h-5"/></button>
              </div>
              <div className="p-6 space-y-4">
                {[
                  {label:"Nom de l'application",placeholder:'Ex: SOIL-AIFASA',type:'text'},
                  {label:'Catégorie',placeholder:'Ex: Analyse, Diagnostic...',type:'text'},
                  {label:'Description courte',placeholder:'En une ou deux phrases...',type:'textarea'},
                  {label:'Lien de démonstration (optionnel)',placeholder:'https://...',type:'url'}
                ].map((field,i)=>(
                  <div key={i}>
                    <label className={LABEL}>{field.label}</label>
                    {field.type==='textarea'
                      ? <textarea rows={3} placeholder={field.placeholder} className="w-full text-sm p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none resize-none"/>
                      : <input type={field.type} placeholder={field.placeholder} className="w-full text-sm p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"/>
                    }
                  </div>
                ))}
                <p className="text-xs text-gray-400 flex items-start gap-2"><FiInfo className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-600"/>Votre soumission sera examinée par le Bureau Exécutif dans les 15 jours ouvrables.</p>
                <button className="w-full bg-gradient-to-r from-emerald-700 to-green-700 hover:opacity-90 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg"><FiSend className="w-4 h-4"/>Envoyer la proposition</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Applications;